import { useEffect, useState, type FormEvent } from 'react'
import { usePageData } from '@/hooks/usePageData'
import FlipButton from '@/components/ui/FlipButton'
import type { ArtworkUpload } from '@/types/artwork'

/*
  Lease Enquiry page — accessible from the Artwork detail page "Or Lease" CTA.
  URL: /lease-enquiry?product={product_uuid}

  No Figma design — follows the MIG style guide and mirrors the Contact page
  layout:
    - Page title h1-title (180px alfarn-2 700)
    - Artwork summary block (thumb + name + artist)
    - Form rows: label 440px Poppins Light + input, 1px divider per row
    - Send button: black bg uppercase Poppins Light 40px
    - BACK button: Poppins Bold 60px → /marketplace/{artwork_slug}

  Submission: React builds a plain form with the user's values plus the
  hidden product UUID, authenticity_token, and form_id (read from the
  hidden Insites form in the layout), then POSTs to the Insites form's
  action URL. The platform pipeline runs server-side — validation, DB
  record, CRM contact creation (via lease_enquiry_event_stream callback),
  workflow + autoresponder emails, and redirect to /thank-you.

  Styling lives in src/styles/pages/lease-enquiry.css.
*/

interface LeaseEnquiryProduct {
  id?: string
  uuid?: string
  name?: string
  slug?: string
  price?: number | null
  main_image?: ArtworkUpload | null
}

interface LeaseEnquiryArtist {
  id?: string
  name?: string
  slug?: string
}

interface LeaseEnquiryPageData {
  product_uuid: string
  product: LeaseEnquiryProduct | null
  artist: LeaseEnquiryArtist | null
}

interface FormState {
  name: string
  email_address: string
  phone_number: string
  message: string
}

type Field = keyof FormState

const FIELD_LABELS: Record<Field, string> = {
  name: 'NAME',
  email_address: 'EMAIL ADDRESS',
  phone_number: 'PHONE NUMBER',
  message: 'MESSAGE',
}

const FIELD_PLACEHOLDERS: Record<Field, string> = {
  name: 'Enter full name',
  email_address: 'Enter email address',
  phone_number: 'Enter phone number',
  message: 'Message',
}

function validate(state: FormState): Partial<Record<Field, string>> {
  const errors: Partial<Record<Field, string>> = {}
  if (!state.name.trim()) errors.name = 'Full name is required.'
  if (!state.email_address.trim()) {
    errors.email_address = 'Email is required.'
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email_address.trim())) {
    errors.email_address = 'Please enter a valid email.'
  }
  if (!state.phone_number.trim()) errors.phone_number = 'Phone number is required.'
  return errors
}

function formatPhone(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''

  if (trimmed.startsWith('+') && trimmed.includes(' ')) return trimmed

  if (trimmed.startsWith('+')) {
    const digits = trimmed.slice(1)
    const codeLen = digits.startsWith('1') && digits.length >= 11 ? 1 : 2
    const code = '+' + digits.slice(0, codeLen)
    const rest = digits.slice(codeLen)
    if (rest.length > 0) return `${code} ${rest}`
    return trimmed
  }

  return `+61 ${trimmed}`
}

function pickImageUrl(up?: ArtworkUpload | null): string | null {
  if (!up) return null
  return up.versions?.card || up.versions?.large || up.url || null
}

function formatPrice(price: number | null | undefined): string {
  if (price == null || Number.isNaN(price)) return ''
  const rounded = Math.round(price)
  return `$${rounded.toLocaleString()}`
}

/**
 * Build a brand-new hidden <form> with the user's values plus the product
 * UUID, and submit it to the hidden Insites form's action URL. Avoids web
 * components entirely — no ElementInternals surprises on submit.
 */
function submitLeaseEnquiryForm(values: FormState, productUuid: string): boolean {
  const mount = document.getElementById('insites-form-mount')
  if (!mount) return false

  const srcForm = mount.querySelector('form') as HTMLFormElement | null
  if (!srcForm) return false

  const token =
    (srcForm.querySelector('input[name="authenticity_token"]') as HTMLInputElement)?.value ?? ''
  const formId =
    (srcForm.querySelector('input[name="form_id"]') as HTMLInputElement)?.value ?? ''

  if (!token || !formId) return false

  const form = document.createElement('form')
  form.method = 'POST'
  form.action = srcForm.action
  form.acceptCharset = 'UTF-8'
  form.style.display = 'none'

  const addField = (name: string, value: string) => {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    form.appendChild(input)
  }

  addField('utf8', '✓')
  addField('authenticity_token', token)
  addField('form_id', formId)

  addField('form[properties_attributes][name]', values.name.trim())
  addField('form[properties_attributes][email_address]', values.email_address.trim())
  addField('form[properties_attributes][phone_number]', formatPhone(values.phone_number))
  addField('form[properties_attributes][message]', values.message.trim())
  addField('form[properties_attributes][product]', productUuid)

  document.body.appendChild(form)
  form.submit()
  return true
}

export default function LeaseEnquiry() {
  const data = usePageData<LeaseEnquiryPageData>()
  const product = data?.product ?? null
  const artist = data?.artist ?? null
  const productUuid = data?.product_uuid ?? ''

  const [visible, setVisible] = useState(false)
  const [state, setState] = useState<FormState>({
    name: '',
    email_address: '',
    phone_number: '',
    message: '',
  })
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(timer)
  }, [])

  const handleChange =
    (field: Field) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setState((prev) => ({ ...prev, [field]: e.target.value }))
      if (errors[field]) {
        setErrors((prev) => {
          const next = { ...prev }
          delete next[field]
          return next
        })
      }
    }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!productUuid) {
      setSubmitError('Missing artwork reference. Please return to the artwork and try again.')
      return
    }

    const fieldErrors = validate(state)
    setErrors(fieldErrors)
    if (Object.keys(fieldErrors).length > 0) return

    setSubmitting(true)
    setSubmitError(null)

    const submitted = submitLeaseEnquiryForm(state, productUuid)
    if (!submitted) {
      setSubmitError('Form system not ready. Please refresh the page and try again.')
      setSubmitting(false)
    }
  }

  const thumbUrl = pickImageUrl(product?.main_image)
  const backHref = product?.slug ? `/marketplace/${product.slug}` : '/marketplace'

  return (
    <section className="mig-page-section">
      <div className="mig-page-title-row" data-visible={visible}>
        <h1 className="mig-lease-enquiry-page-title">Lease Enquiry</h1>
      </div>

      <form onSubmit={handleSubmit} noValidate className="mig-lease-enquiry-form-wrapper">
        {product && (
          <div className="mig-lease-enquiry-artwork">
            <div className="mig-lease-enquiry-artwork-thumb">
              {thumbUrl ? (
                <img
                  src={thumbUrl}
                  alt={product.name ?? 'Artwork'}
                  loading="lazy"
                  decoding="async"
                />
              ) : null}
            </div>
            <div className="mig-lease-enquiry-artwork-info">
              <p className="mig-lease-enquiry-artwork-label">Enquiring about</p>
              {artist?.name && (
                <p className="mig-lease-enquiry-artist-name">{artist.name}</p>
              )}
              {product.name && (
                <p className="mig-lease-enquiry-artwork-name">{product.name}</p>
              )}
              {product.price != null && (
                <p className="mig-lease-enquiry-artwork-price">{formatPrice(product.price)}</p>
              )}
            </div>
          </div>
        )}

        {/* When product lookup failed, show a clear message rather than a broken form */}
        {!product && productUuid === '' && (
          <p className="mig-lease-enquiry-missing-artwork">
            Please return to the <a href="/marketplace">Marketplace</a> and select the artwork you would like to enquire about.
          </p>
        )}

        <div className="mig-lease-enquiry-form-fields">
          {(Object.keys(FIELD_LABELS) as Field[]).map((field) => (
            <LeaseEnquiryFieldRow
              key={field}
              field={field}
              label={FIELD_LABELS[field]}
              placeholder={FIELD_PLACEHOLDERS[field]}
              value={state[field]}
              error={errors[field]}
              onChange={handleChange(field)}
              disabled={submitting}
            />
          ))}
        </div>

        <div className="mig-lease-enquiry-submit-row" data-has-error={!!submitError}>
          {submitError && (
            <p className="mig-lease-enquiry-submit-error" role="alert">{submitError}</p>
          )}
          <FlipButton
            type="submit"
            disabled={submitting || !productUuid}
            className="mig-lease-enquiry-submit-btn"
            data-pending={submitting}
          >
            {submitting ? 'Sending...' : 'Send'}
          </FlipButton>
        </div>

        <div className="mig-page-back-row">
          <a href={backHref} className="mig-lease-enquiry-back-button">BACK</a>
          <div className="mig-divider-line" />
        </div>
      </form>
    </section>
  )
}

function LeaseEnquiryFieldRow({
  field,
  label,
  placeholder,
  value,
  error,
  onChange,
  disabled,
}: {
  field: Field
  label: string
  placeholder: string
  value: string
  error?: string
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  disabled: boolean
}) {
  const isMessage = field === 'message'
  const commonProps = {
    name: field,
    placeholder,
    value,
    onChange,
    disabled,
    className: 'mig-lease-enquiry-input',
    'aria-invalid': !!error,
    'aria-describedby': error ? `${field}-error` : undefined,
  }

  return (
    <div className="mig-lease-enquiry-field-row">
      <div className="mig-lease-enquiry-field-inner" data-multiline={isMessage}>
        <label htmlFor={`lease-enquiry-${field}`} className="mig-lease-enquiry-field-label">
          {label}
        </label>
        {isMessage ? (
          <textarea id={`lease-enquiry-${field}`} rows={3} {...commonProps} />
        ) : (
          <input
            id={`lease-enquiry-${field}`}
            type={
              field === 'email_address'
                ? 'email'
                : field === 'phone_number'
                  ? 'tel'
                  : 'text'
            }
            {...commonProps}
          />
        )}
      </div>
      <div className="mig-divider-line" />
      {error && (
        <p id={`${field}-error`} role="alert" className="mig-lease-enquiry-field-error">
          {error}
        </p>
      )}
    </div>
  )
}
